import { Link, useNavigate } from "react-router-dom";
import { Check, X, ArrowRight, Target, Zap, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { http, formatApiErrorDetail } from "@/lib/api";
import { openCheckout } from "@/lib/razorpay";
import { toast } from "sonner";
import { useState } from "react";
import AffiliateBanner from "@/components/AffiliateBanner";
import SeoHead from "@/components/seo/SeoHead";
import { breadcrumb, offerCatalog, faqPage } from "@/components/seo/schemas";

const PLANS = [
  {
    tier: "free",
    name: "Free",
    blurb: "Try LeadIQ Pro on a few leads, no credit card.",
    price_monthly: 0,
    price_annual_per_month: 0,
    period_label_m: "forever",
    period_label_y: "forever",
    leads: "25 enrichments / month",
    model: "LeadIQ Atom",
    cta: "Start free",
    perks: [
      "Standard render (Playwright + jina)",
      "Tech stack + intent signals",
      "Engagement playbook",
      "CSV upload + export",
      "1 user",
    ],
    locked: ["LeadIQ email verification", "Email finding (no email needed)", "Free funding intel", "Bulk CSV optimisation", "Sharper LLM models"],
    highlight: false,
  },
  {
    tier: "starter",
    name: "Starter",
    blurb: "For solo SDRs running daily prospecting.",
    price_monthly: 29,
    price_annual_per_month: 26,
    period_label_m: "/mo",
    period_label_y: "/mo, billed yearly",
    leads: "300 enrichments / month",
    model: "LeadIQ Atom",
    cta: "Choose Starter",
    perks: [
      "Everything in Free",
      "10× more enrichments",
      "Priority support",
      "1 user (add more at $5/user/mo)",
    ],
    locked: ["LeadIQ email verification", "Email finding", "Free funding intel", "Bulk CSV optimisation"],
    highlight: false,
  },
  {
    tier: "growth",
    name: "Growth",
    blurb: "For sales teams running real outbound campaigns.",
    price_monthly: 149,
    price_annual_per_month: 134,
    period_label_m: "/mo",
    period_label_y: "/mo, billed yearly",
    leads: "1,500 enrichments / month",
    model: "LeadIQ Spark",
    cta: "Choose Growth",
    perks: [
      "Everything in Starter",
      "AI fit scoring (scans your website + ICP wizard)",
      "LeadIQ Spark (5× sharper analysis)",
      "LeadIQ email verification + finder",
      "Free funding intel (unlimited reveals)",
      "Bulk CSV (10× faster uploads)",
      "Scrape-and-infer fallback",
      "Developer API (push leads from your CRM)",
      "3 users (add more at $5/user/mo)",
    ],
    locked: ["LeadIQ Apex (frontier model)", "Multi-product ICP profiles", "Salesforce / HubSpot push"],
    highlight: true,
  },
  {
    tier: "scale",
    name: "Scale",
    blurb: "For revenue teams who treat data as competitive advantage.",
    price_monthly: 599,
    price_annual_per_month: 539,
    period_label_m: "/mo",
    period_label_y: "/mo, billed yearly",
    leads: "6,000 enrichments / month",
    model: "LeadIQ Apex",
    cta: "Choose Scale",
    perks: [
      "Everything in Growth",
      "LeadIQ Apex (frontier model)",
      "Premium reasoning + outreach lines",
      "Salesforce / HubSpot push",
      "Re-enrich every 30 days",
      "5 users (add more at $5/user/mo)",
    ],
    locked: ["Multi-product ICP profiles (Enterprise)", "Unlimited users (Enterprise)"],
    highlight: false,
  },
  {
    tier: "enterprise",
    name: "Enterprise",
    blurb: "For teams running 10K+ leads / month or with strict compliance needs.",
    price_monthly: null,        // null → renders as "Custom"
    price_annual_per_month: null,
    period_label_m: "",
    period_label_y: "",
    leads: "Unlimited enrichments",
    model: "LeadIQ Apex",
    cta: "Talk to sales",
    contact_url: "mailto:sales@leadiq.pro?subject=LeadIQ%20Pro%20Enterprise%20enquiry",
    perks: [
      "Everything in Scale",
      "Unlimited enrichments",
      "Multi-product ICP profiles",
      "Unlimited team seats (SSO/SAML)",
      "Custom Salesforce / HubSpot mappings",
      "Dedicated CSM + 99.9% SLA",
      "On-prem / VPC deployment option",
      "White-glove onboarding",
    ],
    locked: [],
    highlight: false,
  },
];

const COMPARE_ROWS = [
  { feature: "Monthly enrichments", values: ["25", "300", "1,500", "6,000", "Unlimited"] },
  { feature: "Default LLM model", values: ["LeadIQ Atom", "LeadIQ Atom", "LeadIQ Spark", "LeadIQ Apex", "LeadIQ Apex"] },
  { feature: "Web research (Playwright + jina)", values: [true, true, true, true, true] },
  { feature: "Google News + LinkedIn enrichment", values: [true, true, true, true, true] },
  { feature: "CSV upload + export", values: [true, true, true, true, true] },
  { feature: "LeadIQ email verification", values: [false, false, true, true, true] },
  { feature: "LeadIQ email finder (no email needed)", values: [false, false, true, true, true] },
  { feature: "Funding intel (rounds, revenue, HQ)", values: ["1 credit", "1 credit", "Unlimited · free", "Unlimited · free", "Unlimited · free"] },
  { feature: "Bulk enrichment (10× faster CSV)", values: [false, false, true, true, true] },
  { feature: "Scrape-and-infer fallback", values: [false, false, true, true, true] },
  { feature: "Salesforce / HubSpot push", values: [false, false, false, true, true] },
  { feature: "Scheduled re-enrichment (30/60/90d)", values: [false, false, false, true, true] },
  { feature: "Team seats", values: ["1", "1", "3", "Unlimited", "Unlimited"] },
  { feature: "Support", values: ["Community", "Email", "Email", "Priority chat", "Dedicated CSM + SLA"] },
];

const FAQ = [
  {
    q: "What does an 'enrichment' include?",
    a: "Each enrichment turns a lead row (name + company + domain) into a full intelligence package: tech stack, intent signals (hiring/news/funding), buying committee, pain points, an AI-written engagement playbook, and a 0-100 opportunity score. Paid tiers add LeadIQ Pro-verified email + LinkedIn URL.",
  },
  {
    q: "What happens if I exceed my monthly enrichment quota?",
    a: "Pay-as-you-go overage at $0.15 per additional lead, capped at 50% above your plan limit (so a 1,500-lead plan can burst up to 2,250). You'll see live overage cost on the dashboard and we'll email you at 80% / 100% before billing anything extra. Free trial doesn't have overage — upgrade to keep going.",
  },
  {
    q: "Can I switch plans mid-month?",
    a: "Yes — switches are pro-rated. Upgrades take effect immediately; downgrades apply at the next billing cycle.",
  },
  {
    q: "Can I add teammates to my plan?",
    a: "Yes. Starter includes 1 user, Growth includes 3, Scale includes 5, Enterprise is unlimited. You can buy additional users for $5/seat/month on any paid plan from Settings → Team. Extra seats give your teammates access only — they share your monthly enrichment pool, they don't add new credits.",
  },
  {
    q: "Does the AI know what my company sells?",
    a: "Yes — on Growth and above, set up your company profile in 2 minutes. We scan your website (homepage + about page) to extract your positioning, then ask 3 quick questions (target industries, company size, top pain you solve). Every enrichment after that includes a personalized 'Fit score' alongside the generic 'Opportunity score' — so a hot prospect that doesn't match your ICP shows fit 30 even when opportunity is 80. Re-analyze the website anytime to refresh extracted info. Enterprise tier supports multiple ICP profiles for multi-product companies.",
  },
  {
    q: "Why does Scale use a different model?",
    a: "LeadIQ Apex measurably outperforms LeadIQ Spark at structured B2B reasoning — sharper opportunity scoring, more specific outreach lines, and stronger pattern-detection on intent signals. We pass that quality directly to Scale customers.",
  },
  {
    q: "Do you offer annual billing?",
    a: "Yes — pay annually and save 10% on all paid tiers. Toggle 'Annual' above to see the discounted monthly equivalent.",
  },
  {
    q: "Is there an enterprise plan?",
    a: "Yes — custom pricing, unlimited enrichments, dedicated CSM, SSO, on-premises deployment, custom integrations and SLA. Talk to sales.",
  },
  {
    q: "How does AI Outreach billing work?",
    a: "AI Outreach is a separate pay-as-you-go feature available on every plan including Free Trial. $5 buys 100 email credits. 1 credit = 1 AI-drafted email — sending, tracking opens, replies, and edits are all free. Credits never expire and don't reset monthly. You connect your own Gmail / Outlook / Zoho / any SMTP, so all replies and deliverability stay in your inbox.",
  },
];

function Cell({ value, highlight }) {
  if (typeof value === "boolean") {
    return (
      <td className={`px-4 py-3 text-center ${highlight ? "bg-ink-50/40" : ""}`}>
        {value ? (
          <Check className="w-4 h-4 text-[#2E7D32] inline" />
        ) : (
          <X className="w-4 h-4 text-ink-200 inline" />
        )}
      </td>
    );
  }
  return (
    <td className={`px-4 py-3 text-center text-xs font-mono text-ink-700 ${highlight ? "bg-ink-50/40 font-semibold" : ""}`}>
      {value}
    </td>
  );
}

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "annual"

  const handleCta = async (tier) => {
    if (!user) {
      nav("/register");
      return;
    }
    if (tier === "free") {
      toast.info("Free is your default — no payment needed.");
      return;
    }
    if ((user.tier || "free") === tier) {
      toast.info("You're already on this plan.");
      return;
    }
    setBusy(tier);
    try {
      const { data } = await http.post("/billing/tier/checkout", {
        tier,
        billing_cycle: billingCycle,
      });

      if (data.demo) {
        toast.success(data.message || `Switched to ${tier}`);
        await refresh?.();
        nav("/dashboard");
        setBusy(null);
        return;
      }
      if (data.affiliate && data.affiliate.applied) {
        toast.success(
          `${data.affiliate.partner_name} discount applied · ${data.affiliate.discount_pct}% off ($${data.affiliate.discount_amount_usd.toFixed(2)} saved)`,
          { duration: 6000 }
        );
      }
      if (data.tax && (data.tax.tax_amount_usd || 0) > 0) {
        toast.info(
          `Charging $${data.tax.total_usd.toFixed(2)} · $${data.tax.subtotal_usd.toFixed(2)} + ${data.tax.tax_label} $${data.tax.tax_amount_usd.toFixed(2)}`,
          { duration: 5000 }
        );
      }

      await openCheckout({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "LeadIQ Pro",
        description: `${tier.charAt(0).toUpperCase() + tier.slice(1)} plan · ${billingCycle}`,
        prefill: {
          name: data.user?.name || "",
          email: data.user?.email || "",
        },
        theme: { color: "#0a0a0a" },
        modal: { ondismiss: () => setBusy(null) },
        handler: async (resp) => {
          try {
            await http.post("/billing/tier/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Plan upgraded to ${tier.charAt(0).toUpperCase() + tier.slice(1)}`);
            await refresh?.();
            nav("/dashboard");
          } catch (err) {
            toast.error(
              formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed"
            );
          } finally {
            setBusy(null);
          }
        },
        onFailed: (resp) => {
          toast.error("Payment failed: " + (resp?.error?.description || "unknown error"));
          setBusy(null);
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50">
      <SeoHead
        title="Pricing — LeadIQ Pro"
        description="Transparent, pay-only-for-verified-hits pricing. 30-day free trial on every tier. Credit packs, mobile number lookups and AI outreach credits sold à la carte."
        path="/pricing"
        jsonLd={[
          breadcrumb([
            { name: "Home", url: "/" },
            { name: "Pricing", url: "/pricing" },
          ]),
          offerCatalog({
            tiers: [
              { name: "Starter", description: "For solo founders and small sales teams getting started with enriched lead data.", price: "29" },
              { name: "Growth", description: "For scaling sales teams needing high-volume enrichment, intent signals and AI outreach.", price: "99" },
              { name: "Scale", description: "For revenue teams running multi-channel outbound at scale with API + webhooks.", price: "299" },
              { name: "Enterprise", description: "Custom pricing, dedicated success, priority support and SLA.", price: "499" },
            ],
          }),
          faqPage([
            { q: "How is pricing structured?", a: "LeadIQ Pro has four tiers — Starter, Growth, Scale, Enterprise — plus pay-as-you-go credit packs for enrichment, mobile lookups and AI outreach. All plans include a 30-day free trial with no credit card required." },
            { q: "What is a credit?", a: "One credit = one verified enrichment hit. If our 4-layer validator rejects a result, no credit is spent — you only pay for verified data." },
            { q: "Can I buy credits à la carte?", a: "Yes. Enrichment credit packs, mobile number credit packs, and AI outreach email credit packs are all available as one-time purchases on top of any subscription." },
            { q: "Do you offer refunds?", a: "Yes. See our refund policy at /legal/refunds — in short, unused credits within 7 days of purchase are refundable, subject to minimal fair-use review." },
            { q: "Is GST charged for Indian customers?", a: "Yes. Indian customers are charged 18 percent GST on top of the plan price (CGST + SGST for Maharashtra, IGST elsewhere). Non-Indian customers pay zero tax." },
          ]),
        ]}
      />
      <AffiliateBanner />
      {/* slim top nav */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-ink-100 h-16 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand grid place-items-center rounded-sm">
              <Target className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-base tracking-tight text-ink-900">
                LeadIQ Pro
              </div>
              <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
                Lead Enrichment Engine
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="text-xs uppercase tracking-widestx font-semibold text-ink-700 hover:text-brand">
                Back to dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-xs uppercase tracking-widestx font-semibold text-ink-700 hover:text-brand">
                  Sign in
                </Link>
                <Link to="/register">
                  <Button size="sm" className="rounded-sm bg-brand hover:bg-brand-hover text-white">
                    Start free
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-3">
          Pricing · v1
        </div>
        <h1
          data-testid="pricing-headline"
          className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none font-extrabold text-ink-900 max-w-4xl"
        >
          Pay for sharper signal,<br />
          <span className="font-light">not bigger quotas.</span>
        </h1>
        <p className="text-ink-500 text-base mt-6 max-w-2xl leading-relaxed">
          Every plan uses a more powerful AI model and a deeper data stack — outputs measurably get smarter as you upgrade. No seat traps, no per-feature add-ons.
        </p>
      </section>

      {/* plan cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Billing cycle toggle */}
        <div className="flex justify-center mb-8" data-testid="billing-cycle-toggle">
          <div className="inline-flex items-center bg-white border border-ink-100 rounded-sm p-1">
            <button
              data-testid="billing-monthly"
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 text-xs uppercase tracking-widestx font-semibold rounded-sm transition-colors ${
                billingCycle === "monthly"
                  ? "bg-ink-900 text-white"
                  : "text-ink-500 hover:text-ink-900"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setBillingCycle("annual")}
              className={`px-4 py-2 text-xs uppercase tracking-widestx font-semibold rounded-sm transition-colors inline-flex items-center gap-2 ${
                billingCycle === "annual"
                  ? "bg-ink-900 text-white"
                  : "text-ink-500 hover:text-ink-900"
              }`}
            >
              Annual
              <span
                className={`text-[9px] uppercase tracking-widestx font-bold px-1.5 py-0.5 rounded-sm ${
                  billingCycle === "annual"
                    ? "bg-brand text-white"
                    : "bg-[#E8F5E9] text-[#2E7D32]"
                }`}
              >
                Save 10%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {PLANS.map((p) => {
            const isCurrent = user && (user.tier || "free") === p.tier;
            const isCustom = p.price_monthly === null;
            const priceVal =
              billingCycle === "annual" ? p.price_annual_per_month : p.price_monthly;
            const priceStr = isCustom ? "Custom" : priceVal === 0 ? "$0" : `$${priceVal}`;
            const periodStr =
              billingCycle === "annual" ? p.period_label_y : p.period_label_m;
            return (
              <div
                key={p.tier}
                data-testid={`pricing-plan-${p.tier}`}
                className={`relative p-6 border rounded-sm flex flex-col transition-transform hover:-translate-y-0.5 ${
                  p.highlight
                    ? "bg-ink-900 text-white border-ink-900"
                    : "bg-white border-ink-100"
                } ${isCurrent ? "ring-2 ring-brand ring-offset-4 ring-offset-ink-50" : ""}`}
              >
                {p.highlight && (
                  <div className="absolute -top-2.5 left-6 inline-flex items-center gap-1 bg-brand text-white text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
                    <Zap className="w-2.5 h-2.5" /> Most popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-2.5 right-6 bg-brand text-white text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
                    Current
                  </div>
                )}

                <div className="text-[10px] uppercase tracking-widestx font-mono opacity-70">
                  {p.name}
                </div>
                <p className={`text-xs mt-2 leading-relaxed ${p.highlight ? "text-white/70" : "text-ink-500"}`}>
                  {p.blurb}
                </p>

                <div className="mt-5 flex items-baseline gap-1">
                  <span
                    className={`font-display font-black tracking-tighter ${
                      isCustom ? "text-3xl" : "text-5xl"
                    }`}
                  >
                    {priceStr}
                  </span>
                  {!isCustom && (
                    <span className={`text-xs ${p.highlight ? "text-white/60" : "text-ink-400"}`}>
                      {periodStr}
                    </span>
                  )}
                  {isCustom && (
                    <span className={`text-xs ${p.highlight ? "text-white/60" : "text-ink-400"}`}>
                      pricing
                    </span>
                  )}
                </div>
                {billingCycle === "annual" && !isCustom && p.price_monthly > 0 && (
                  <div
                    className={`text-[10px] mt-1 ${
                      p.highlight ? "text-white/60" : "text-ink-400"
                    }`}
                  >
                    <span className="line-through">${p.price_monthly}/mo</span>{" "}
                    <span className="text-[#2E7D32] font-semibold">save 10%</span>
                  </div>
                )}
                {isCustom && (
                  <div className={`text-[10px] mt-1 ${p.highlight ? "text-white/60" : "text-ink-400"}`}>
                    Tailored to your volume
                  </div>
                )}
                <div className={`text-xs font-mono mt-2 ${p.highlight ? "text-white/80" : "text-ink-500"}`}>
                  {p.leads}
                </div>

                <div
                  className={`mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-mono px-2 py-1 rounded-sm w-fit ${
                    p.highlight ? "bg-brand/30 text-white" : "bg-ink-100 text-ink-700"
                  }`}
                >
                  <span className="opacity-70">model:</span>
                  <span className="font-semibold normal-case tracking-normal">{p.model}</span>
                </div>

                <ul className="space-y-2 mt-5 flex-1">
                  {p.perks.map((perk, i) => (
                    <li key={i} className="text-xs flex items-start gap-2">
                      <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${p.highlight ? "text-brand" : "text-[#2E7D32]"}`} />
                      <span className={p.highlight ? "text-white/90" : "text-ink-700"}>{perk}</span>
                    </li>
                  ))}
                  {p.locked.map((perk, i) => (
                    <li key={`l${i}`} className="text-xs flex items-start gap-2 opacity-40">
                      <X className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span className={p.highlight ? "text-white/60" : "text-ink-400 line-through"}>{perk}</span>
                    </li>
                  ))}
                </ul>

                {p.contact_url ? (
                  <a
                    href={p.contact_url}
                    data-testid={`pricing-cta-${p.tier}`}
                    className={`mt-6 w-full text-xs font-medium uppercase tracking-widestx py-3 rounded-sm text-center transition-colors ${
                      p.highlight
                        ? "bg-brand hover:bg-brand-hover text-white"
                        : "bg-ink-900 hover:bg-ink-700 text-white"
                    }`}
                  >
                    {p.cta}
                  </a>
                ) : (
                  <button
                    data-testid={`pricing-cta-${p.tier}`}
                    onClick={() => handleCta(p.tier)}
                    disabled={busy !== null || isCurrent}
                    className={`mt-6 w-full text-xs font-medium uppercase tracking-widestx py-3 rounded-sm transition-colors disabled:opacity-50 ${
                      p.highlight
                        ? "bg-brand hover:bg-brand-hover text-white"
                        : "bg-ink-100 hover:bg-ink-200 text-ink-900"
                    }`}
                  >
                    {busy === p.tier ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                    ) : isCurrent ? (
                      "Current plan"
                    ) : (
                      p.cta
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* AI Outreach band — universal pay-as-you-go */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="pricing-outreach-band">
        <div className="bg-ink-900 text-white rounded-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            <div className="lg:col-span-2 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-brand mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                New · works on every plan
              </div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tight font-extrabold leading-tight mb-3">
                AI Outreach.<br />
                <span className="font-light text-white/70">Pay only for what you send.</span>
              </h3>
              <p className="text-sm text-white/60 leading-relaxed mb-6">
                Connect your Gmail / Outlook / any SMTP. We draft personalized cold emails from your enriched lead data.
                You hit send — replies, deliverability, and tracking stay in your inbox.
              </p>
              <div className="flex flex-wrap gap-2 text-[11px] font-mono text-white/70">
                <span className="px-2 py-1 border border-white/15 rounded-sm">3 tones</span>
                <span className="px-2 py-1 border border-white/15 rounded-sm">Pre-fills committee</span>
                <span className="px-2 py-1 border border-white/15 rounded-sm">Edit before send</span>
                <span className="px-2 py-1 border border-white/15 rounded-sm">Encrypted creds</span>
              </div>
            </div>

            <div className="lg:col-span-3 p-8 lg:p-10">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-white/50 mb-4">
                Email credit packs · pay-as-you-go
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { credits: 100, price: 5, label: "Starter" },
                  { credits: 500, price: 25, label: "Growth" },
                  { credits: 2000, price: 100, label: "Pro" },
                  { credits: 10000, price: 500, label: "Bulk" },
                ].map((p) => (
                  <div
                    key={p.label}
                    data-testid={`outreach-pack-${p.label.toLowerCase()}`}
                    className="border border-white/10 rounded-sm p-4 hover:border-brand transition-colors"
                  >
                    <div className="text-[10px] uppercase tracking-widestx font-mono text-white/50 mb-1">
                      {p.label}
                    </div>
                    <div className="font-display font-extrabold text-2xl mb-1">${p.price}</div>
                    <div className="text-[11px] font-mono text-white/60">
                      {p.credits.toLocaleString()} credits<br />
                      ${(p.price / p.credits).toFixed(3)}/email
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-5 border-t border-white/10">
                <p className="text-[11px] text-white/50 leading-relaxed max-w-xl">
                  1 credit = 1 AI-drafted email. Sending, tracking opens, and replies are free.
                  Credits never expire. Available on Free, Atom, Spark, Apex, and Enterprise.
                </p>
                <Link
                  to={user ? "/settings/outreach" : "/register"}
                  data-testid="pricing-outreach-cta"
                  className="shrink-0 inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs uppercase tracking-widestx font-semibold px-4 py-3 rounded-sm transition-colors"
                >
                  {user ? "Open AI Outreach" : "Start free → buy credits later"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* compare table */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-3">
          Compare every feature
        </div>
        <h2 className="font-display text-3xl tracking-tight font-extrabold text-ink-900 mb-8">
          What's in each plan.
        </h2>

        <div className="bg-white border border-ink-100 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-100">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widestx font-semibold text-ink-500 w-1/3">
                  Feature
                </th>
                {["Free", "Starter", "Growth", "Scale", "Enterprise"].map((n, i) => (
                  <th
                    key={n}
                    className={`px-4 py-3 text-[10px] uppercase tracking-widestx font-semibold text-ink-700 text-center ${
                      n === "Growth" ? "bg-ink-100" : ""
                    }`}
                  >
                    {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, idx) => (
                <tr key={row.feature} className={idx % 2 === 0 ? "bg-white" : "bg-ink-50/30"}>
                  <td className="px-4 py-3 text-xs text-ink-700 font-medium">{row.feature}</td>
                  {row.values.map((v, i) => (
                    <Cell key={i} value={v} highlight={i === 2} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-3">
          Common questions
        </div>
        <h2 className="font-display text-3xl tracking-tight font-extrabold text-ink-900 mb-8">
          Frequently asked.
        </h2>
        <div className="space-y-6">
          {FAQ.map((f, i) => (
            <div key={i} data-testid={`faq-${i}`} className="border-b border-ink-100 pb-6 last:border-0">
              <h3 className="font-display font-extrabold text-lg text-ink-900 tracking-tight">
                {f.q}
              </h3>
              <p className="text-sm text-ink-600 mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* footer cta */}
      <section className="bg-ink-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-extrabold">
            Stop chasing.<br />
            <span className="font-light">Start qualifying.</span>
          </h2>
          <p className="text-white/70 text-sm mt-4 max-w-xl mx-auto">
            Try LeadIQ Pro on 25 leads, no card required. Upgrade only when you're sure.
          </p>
          <div className="mt-8 inline-flex">
            {user ? (
              <Link to="/dashboard">
                <Button size="lg" className="rounded-sm bg-white text-ink-900 hover:bg-white/90 font-medium">
                  Open dashboard
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button size="lg" className="rounded-sm bg-white text-ink-900 hover:bg-white/90 font-medium">
                  Start free — 25 leads
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
